<script lang="ts" setup>
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'

interface Props {
  isOpen: boolean
  oriIndustry: string
}

const props = defineProps<Props>()
const emit = defineEmits(['update:modelValue', 'update:open'])
const breakpoints = useBreakpoints(breakpointsTailwind)
const mdAndSmaller = breakpoints.smallerOrEqual('lg')
const open = ref(false)
const isFullscreen = ref(false)
const filteredGICS = ref([])

// setup GICS
const gicsIndustry = useGetGICS()
filteredGICS.value = gicsIndustry

watch(() => props.isOpen, (value) => {
  if (value)
    openModal()
  else
    closeModal()
})

const selectGICSKeyword = ref()
const selectedGICS = computed({
  get: () => useTextCapitalize(useTextToParam(props.oriIndustry, true)),
  set: (value) => {
    emit('update:modelValue', value)
    selectGICSKeyword.value = ''
  },
})

const getSuggestion = useDebounceFn(async (val) => {
  selectGICSKeyword.value = val
  filteredGICS.value = !selectGICSKeyword.value
    ? gicsIndustry
    : gicsIndustry.filter((item: any) => { return item.value.includes(selectGICSKeyword.value.toLowerCase()) })
}, 500)

function onEnter() {
  if (selectGICSKeyword.value !== '') {
    emit('update:modelValue', selectGICSKeyword.value)
    selectGICSKeyword.value = ''
  }
}

function resetValue() {
  selectedGICS.value = null
}

function toogleFullScreen() {
  isFullscreen.value = !isFullscreen.value
}

function closeModal() {
  open.value = false
  emit('update:open', false)
}

function openModal() {
  open.value = true
}
</script>

<template>
  <div class="h-full flex flex-col">
    <div v-if="mdAndSmaller">
      <TransitionRoot as="template" appear :show="open">
        <Dialog as="div" class="relative z-100" @close="closeModal">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <div class="fixed inset-0 bg-black bg-opacity-25" />
          </TransitionChild>

          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              :class="[isFullscreen ? 'max-h-full min-h-full' : 'max-h-4/5 min-h-4/5 rounded-t-3xl !overflow-y-hidden']"
              class="fixed inset-x-0 bottom-0 overflow-y-auto bg-white border-t border-gray-100 rounded-t-3xl overflow-y-scroll bg-white py-4 px-4 sm:p-6 text-left align-middle shadow transition-all"
            >
              <!-- header -->
              <div class="justify-between mb-2">
                <div class="text-center" @click="toogleFullScreen()">
                  <Icon name="pepicons-pop:line-x" class="w-7 h-7 mx-auto text-gray-400" />
                </div>
                <div class="flex items-center">
                  <div class="text-xl font-bold">
                    Industries
                  </div>
                  <div v-if="!isEmpty(selectedGICS) && selectedGICS.toLowerCase() !== 'all'" class="text-[#5843E5] ml-auto text-sm cursor-pointer font-semibold" @click="resetValue()">
                    Reset
                  </div>
                </div>
              </div>

              <!-- search bar -->
              <div class="mb-2">
                <div class="flex items-center bg-gray-[#F9FAFB] px-2 rounded-md h-8 border-1 border-gray-200">
                  <Icon name="mdi:magnify" class="w-4 h-4 mr-1" />
                  <input v-model="selectGICSKeyword" class="focus:ring-0 focus:outline-none w-[80%] bg-transparent" @input="getSuggestion($event.target.value)">
                </div>
              </div>

              <div class="overflow-y-scroll w-full h-[65vh] pb-2 text-sm">
                <div v-for="(item, idx) in filteredGICS" :key="idx" :class="`${!isEmpty(selectedGICS) && item.value === selectedGICS.toLowerCase() ? 'bg-primary-50' : ''} cursor-pointer select-none py-4 px-2 border-b-1 focus:outline-none hover:bg-primary-50`" @click="selectedGICS = item.value; emit('update:modelValue', item.value)">
                  {{ item.name }}
                </div>
                <div v-if="isEmpty(filteredGICS)" class="cursor-pointer select-none py-4 px-2 border-b-1 focus:outline-none">
                  No Results
                </div>
                <div v-if="gicsIndustry.length > 20" class="select-none py-4 px-2 focus:outline-none text-center text-gray-400 text-[10px]">
                  Can't find the industry? <br>
                  Try using the search bar
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </TransitionRoot>
    </div>
    <div v-else class="flex-1 h-full">
      <Combobox v-model="selectedGICS" nullable>
        <div class="relative">
          <ComboboxButton :class="`${(!isEmpty(selectedGICS) && selectedGICS.toLowerCase() !== 'all') ? 'border-primary-400 bg-[#ECE9FC] text-[#5843E5] font-semibold' : 'bg-white border-gray-200'} relative pl-4 pr-10 py-1.5 rounded-md text-sm border`">
            <span class="block truncate">{{ (!isEmpty(gicsIndustry) && !isEmpty(selectedGICS) && selectedGICS.toLowerCase() !== 'all') ? gicsIndustry.find((obj: any) => obj.value === selectedGICS.toLowerCase())?.name : 'Industries' }} </span>
            <div class="absolute inset-y-0 right-0 flex flex-shrink-0 items-center pr-2">
              <Icon name="heroicons:chevron-up-down-solid" class="h-4 w-4 text-gray-400" aria-hidden="true" />
            </div>
          </ComboboxButton>
          <TransitionRoot
            leave="transition ease-in duration-100"
            leave-from="opacity-100"
            leave-to="opacity-0"
            @after-leave="selectGICSKeyword = ''"
          >
            <ComboboxOptions class="fixed lg:w-72 h-80 overflow-y-scroll rounded-md bg-white shadow-lg mt-1 shadow-lg px-4 pb-4 bg-white focus:outline-none absolute">
              <div class="sticky top-0 pb-1 pt-4 bg-white mb-3.5">
                <div class="flex items-center mb-2">
                  <div class="font-bold text-xl">
                    Industries
                  </div>
                  <div v-if="!isEmpty(selectedGICS) && selectedGICS.toLowerCase() !== 'all'" class="text-[#5843E5] ml-auto text-sm cursor-pointer font-semibold" @click="resetValue()">
                    Reset
                  </div>
                </div>
                <div class="flex items-center bg-gray-[#F9FAFB] px-2 rounded-md h-8 border-1 border-gray-200">
                  <div class="flex-shrink-0 flex items-center">
                    <Icon name="mdi:magnify" class="w-4 h-4 mr-1" />
                  </div>
                  <ComboboxInput :display-value="() => selectGICSKeyword ? selectGICSKeyword : ''" placeholder="Search industry" class="p-0 placeholder-gray-400 font-medium focus:ring-0 focus:outline-none w-full h-full border-0 text-xs" @keyup.enter="onEnter" @change="getSuggestion($event.target.value)" />
                </div>
              </div>
              <div v-if="filteredGICS.length === 0 && selectGICSKeyword !== ''" class="relative cursor-default select-none px-4 py-2 text-gray-700">
                Nothing found.
              </div>

              <ComboboxOption v-for="(item, idx) in filteredGICS" :key="idx" v-slot="{ active }" as="template" :value="item.value" class="px-2">
                <li :class="`${idx < (filteredGICS.length - 1) ? 'border-b-1' : ''} cursor-pointer select-none py-4 focus:outline-none hover:bg-primary-50 text-sm ${(active || (!isEmpty(selectedGICS) && selectedGICS.toLowerCase()) === item.value) ? 'bg-primary-50' : ''}`">
                  {{ item.name }}
                </li>
              </ComboboxOption>
            </ComboboxOptions>
          </TransitionRoot>
        </div>
      </Combobox>
    </div>
  </div>
</template>
